import { supabase } from '../lib/supabase';
import { aporteExternoService } from './aporteExternoService';
import { fechaInicioMes, fechaFinMes } from '../utils/dateHelpers';

export interface ResumenCuotaEntidad {
  entidad_id: string;
  entidad_nombre: string;
  total_recaudado: number;
  total_transferido: number;
  saldo_pendiente: number;
}

export interface RecepcionCuotaPayload {
  monto: number;
  fecha: string;
  descripcion?: string;
  registrado_por_usuario_id: string;
  entidad_origen_tipo: 'lumisial' | 'diocesis';
  entidad_origen_id: string;
  entidad_destino_id: string;
}

function buildFechaRange(mes?: number, anio?: number): { fechaInicio?: string; fechaFin?: string } {
  if (!anio) return {};
  if (mes) return { fechaInicio: fechaInicioMes(mes, anio), fechaFin: fechaFinMes(mes, anio) };
  return { fechaInicio: `${anio}-01-01`, fechaFin: `${anio}-12-31` };
}

export const aporteExternoCuotaService = {
  /**
   * Obtener resumen de cuota iglesia por entidades subordinadas.
   * Para admin diocesano: resumen por lumisiales de su diócesis.
   * Para admin nacional: resumen por diócesis de su provincia eclesiástica.
   */
  async getResumenPorEntidades(filtros: {
    diocesisId?: string;
    provinciaEclesiasticaId?: string;
    mes?: number;
    anio?: number;
  }): Promise<ResumenCuotaEntidad[]> {
    const { mes, anio } = filtros;
    if (filtros.diocesisId) {
      return this.getResumenPorLumisiales(filtros.diocesisId, mes, anio);
    }
    if (filtros.provinciaEclesiasticaId) {
      return this.getResumenPorDiocesis(filtros.provinciaEclesiasticaId, mes, anio);
    }
    return [];
  },

  /**
   * Resumen de cuota iglesia por lumisiales de una diócesis.
   * total_recaudado: cuota iglesia recaudada localmente por cada lumisial (movimientos de miembros).
   * total_transferido: cuota iglesia recibida por la diócesis desde cada lumisial (registros externos).
   * saldo_pendiente: total_recaudado - total_transferido.
   */
  async getResumenPorLumisiales(diocesisId: string, mes?: number, anio?: number): Promise<ResumenCuotaEntidad[]> {
    const { fechaInicio, fechaFin } = buildFechaRange(mes, anio);

    // Obtener lumisiales de la diócesis
    const { data: lumisiales, error: lumError } = await supabase
      .from('lumisiales')
      .select('id, nombre')
      .eq('diocesis_id', diocesisId)
      .eq('activo', true);

    if (lumError) throw lumError;
    if (!lumisiales || lumisiales.length === 0) return [];

    const lumisialIds = lumisiales.map(l => l.id);

    // Obtener cuota iglesia recaudada localmente por cada lumisial
    let recQuery = supabase
      .from('movimientos')
      .select('lumisial_id, monto')
      .in('lumisial_id', lumisialIds)
      .eq('tipo', 'ingreso')
      .eq('origen_tipo', 'miembro')
      .eq('categoria_aporte', 'cuota_iglesia')
      .eq('anulado', false)
      .is('es_registro_externo', null);
    if (fechaInicio) recQuery = recQuery.gte('fecha', fechaInicio);
    if (fechaFin) recQuery = recQuery.lte('fecha', fechaFin);

    const { data: recaudados, error: recError } = await recQuery;
    if (recError) throw recError;

    // Obtener transferencias recibidas por la diócesis desde cada lumisial
    // (creadas por la RPC recibir_balance_con_cuota_iglesia al recibir el balance del Lumisial)
    let transQuery = supabase
      .from('movimientos')
      .select('origen_id, monto')
      .eq('diocesis_id', diocesisId)
      .eq('tipo', 'ingreso')
      .eq('categoria_aporte', 'cuota_iglesia')
      .eq('origen_tipo', 'lumisial')
      .eq('anulado', false);
    if (fechaInicio) transQuery = transQuery.gte('fecha', fechaInicio);
    if (fechaFin) transQuery = transQuery.lte('fecha', fechaFin);

    const { data: transferidos, error: transError } = await transQuery;

    if (transError) throw transError;

    // Agrupar recaudado por lumisial
    const recaudadoMap = new Map<string, number>();
    for (const m of (recaudados || [])) {
      const lumId = m.lumisial_id;
      recaudadoMap.set(lumId, (recaudadoMap.get(lumId) || 0) + Number(m.monto));
    }

    // Agrupar transferido por lumisial origen
    const transferidoMap = new Map<string, number>();
    for (const m of (transferidos || [])) {
      const origenId = m.origen_id as string;
      transferidoMap.set(origenId, (transferidoMap.get(origenId) || 0) + Number(m.monto));
    }

    return lumisiales.map(l => {
      const totalRecaudado = recaudadoMap.get(l.id) || 0;
      const totalTransferido = transferidoMap.get(l.id) || 0;
      return {
        entidad_id: l.id,
        entidad_nombre: l.nombre,
        total_recaudado: totalRecaudado,
        total_transferido: totalTransferido,
        saldo_pendiente: totalRecaudado - totalTransferido,
      };
    });
  },

  /**
   * Resumen de cuota iglesia por diócesis de una provincia eclesiástica.
   * total_recaudado: cuota iglesia recibida por cada diócesis desde sus lumisiales.
   * total_transferido: cuota iglesia recibida por la provincia desde cada diócesis.
   * saldo_pendiente: total_recaudado - total_transferido.
   */
  async getResumenPorDiocesis(provinciaEclesiasticaId: string, mes?: number, anio?: number): Promise<ResumenCuotaEntidad[]> {
    const { fechaInicio, fechaFin } = buildFechaRange(mes, anio);

    // Obtener diócesis de la provincia eclesiástica
    const { data: diocesisList, error: dioError } = await supabase
      .from('diocesis')
      .select('id, nombre')
      .eq('provincia_eclesiastica_id', provinciaEclesiasticaId)
      .eq('activo', true);

    if (dioError) throw dioError;
    if (!diocesisList || diocesisList.length === 0) return [];

    const diocesisIds = diocesisList.map(d => d.id);

    // Obtener cuota iglesia recibida por cada diócesis (registros externos desde lumisiales)
    let recQuery = supabase
      .from('movimientos')
      .select('diocesis_id, monto')
      .in('diocesis_id', diocesisIds)
      .eq('tipo', 'ingreso')
      .eq('categoria_aporte', 'cuota_iglesia')
      .eq('es_registro_externo', true)
      .eq('entidad_origen_tipo', 'lumisial')
      .eq('anulado', false);
    if (fechaInicio) recQuery = recQuery.gte('fecha', fechaInicio);
    if (fechaFin) recQuery = recQuery.lte('fecha', fechaFin);

    const { data: recaudados, error: recError } = await recQuery;
    if (recError) throw recError;

    // Obtener transferencias recibidas por la provincia desde cada diócesis
    // (creadas por la RPC recibir_balance_con_cuota_iglesia al recibir el balance de la diócesis)
    let transQuery = supabase
      .from('movimientos')
      .select('origen_id, monto')
      .eq('provincia_eclesiastica_id', provinciaEclesiasticaId)
      .eq('tipo', 'ingreso')
      .eq('categoria_aporte', 'cuota_iglesia')
      .eq('origen_tipo', 'diocesis')
      .eq('anulado', false);
    if (fechaInicio) transQuery = transQuery.gte('fecha', fechaInicio);
    if (fechaFin) transQuery = transQuery.lte('fecha', fechaFin);

    const { data: transferidos, error: transError } = await transQuery;

    if (transError) throw transError;

    // Agrupar recaudado por diócesis
    const recaudadoMap = new Map<string, number>();
    for (const m of (recaudados || [])) {
      const dioId = m.diocesis_id as string;
      recaudadoMap.set(dioId, (recaudadoMap.get(dioId) || 0) + Number(m.monto));
    }

    // Agrupar transferido por diócesis origen
    const transferidoMap = new Map<string, number>();
    for (const m of (transferidos || [])) {
      const origenId = m.origen_id as string;
      transferidoMap.set(origenId, (transferidoMap.get(origenId) || 0) + Number(m.monto));
    }

    return diocesisList.map(d => {
      const totalRecaudado = recaudadoMap.get(d.id) || 0;
      const totalTransferido = transferidoMap.get(d.id) || 0;
      return {
        entidad_id: d.id,
        entidad_nombre: d.nombre,
        total_recaudado: totalRecaudado,
        total_transferido: totalTransferido,
        saldo_pendiente: totalRecaudado - totalTransferido,
      };
    });
  },

  /**
   * Reutiliza la validación de jurisdicción de aporteExternoService.
   */
  validarJurisdiccion: aporteExternoService.validarJurisdiccion,

  /**
   * Reutiliza la validación de jurisdicción async de aporteExternoService.
   */
  validarJurisdiccionAsync: aporteExternoService.validarJurisdiccionAsync.bind(aporteExternoService),
};
